import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { LANDING_VARIATION_KEY, withLandingVariation } from './vsl-experiment';

export const TYPEFORM_URL = 'https://taoh.typeform.com/to/ThWEBxIi';
const STORAGE_KEY = 'taoh_tracking_params';
const COOKIE_KEY = 'taoh_tp';
const MAX_AGE = 60 * 60 * 24 * 90;

type Params = Record<string, string>;

/** Test/debug switches that must never be copied from storage back into a URL. */
export const NEVER_RESTORE = ['notrack', 'nocache', 'nc', 'booking_test_cancel'];

const readCookie = (name: string): string | null => {
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

export const getStoredParams = (): Params => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) ?? readCookie(COOKIE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const out = parsed && typeof parsed === 'object' ? parsed : {};
    delete out.cta; // VSL-only A/B tag — never restored site-wide
    return out;
  } catch {
    return {};
  }
};

const urlParams = (search = window.location.search): Params => {
  const out: Params = {};
  new URLSearchParams(search).forEach((v, k) => { if (v) out[k] = v; });
  return out;
};

/** Merge non-empty URL params into storage (localStorage + 90-day cookie). */
export const captureParams = (search = window.location.search): Params => {
  const incoming = urlParams(search);
  delete incoming.cta; // VSL-only A/B tag — never stored or restored site-wide
  const merged = { ...getStoredParams(), ...incoming };
  try {
    const json = JSON.stringify(merged);
    localStorage.setItem(STORAGE_KEY, json);
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(json)}; max-age=${MAX_AGE}; path=/; SameSite=Lax`;
  } catch { /* storage unavailable */ }
  return merged;
};

/** Stored params overlaid by current URL params (URL wins). */
export const getAllParams = (): Params => ({ ...getStoredParams(), ...urlParams() });

/**
 * Only same-origin page links may carry the landing test tag.
 * Outbound destinations (Instagram, WhatsApp) and device handlers (tel:, sms:, mailto:)
 * must keep their exact URL with nothing appended.
 */
export const isInternalLink = (
  href: string,
  base = typeof window === 'undefined' ? 'https://thealphaomegahub.com' : window.location.origin,
): boolean => {
  try {
    const url = new URL(href, base);
    return url.origin === base && (url.protocol === 'http:' || url.protocol === 'https:');
  } catch {
    return false;
  }
};

export const buildTypeformUrl = (): string => {
  const qs = new URLSearchParams(getAllParams());
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc');
  if (fbp) qs.set('fbp', fbp);
  if (fbc) qs.set('fbc', fbc);
  const s = qs.toString();
  return s ? `${TYPEFORM_URL}?${s}` : TYPEFORM_URL;
};

/** The address bar's query string, or "" when there is no window (build time). */
const currentSearch = (): string => (typeof window === 'undefined' ? '' : window.location.search);

/**
 * Adds every query parameter of the current address bar to a destination URL,
 * keeping parameters the URL already carries and adding no "?" when there is nothing to append.
 */
export const withPageParams = (url: string, search = currentSearch()): string => {
  const qs = search.replace(/^\?+/, '');
  if (!qs) return url;
  const mark = url.indexOf('?');
  const base = mark === -1 ? url : url.slice(0, mark);
  const merged = new URLSearchParams(mark === -1 ? '' : url.slice(mark + 1));
  new URLSearchParams(qs).forEach((value, key) => {
    if (!merged.has(key)) merged.append(key, value);
  });
  const out = merged.toString();
  return out ? `${base}?${out}` : base;
};

export const pushCtaClick = (location: string) => {
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event: 'cta_typeform_click', cta_location: location, page_path: window.location.pathname });
};

/** Mounted once: captures params on every route change and restores missing ones in the URL. */
export const useTrackingParams = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // The booking iframe may only receive attribution present in this visit's URL.
    if (pathname.replace(/\/+$/, '').toLowerCase() === '/book') return;
    const stored = captureParams(search);
    // Funnel pages carry only the params already in the URL (plus the landing tag via links).
    const funnel = /^\/(vsl(-a|-b)?|filled)$/.test(pathname.replace(/\/+$/, '').toLowerCase());
    if (funnel) Object.keys(stored).forEach((k) => delete stored[k]);
    NEVER_RESTORE.forEach((k) => delete stored[k]);
    const current = new URLSearchParams(window.location.search);
    let changed = false;
    Object.entries(stored).forEach(([k, v]) => {
      if (!current.has(k)) { current.set(k, v); changed = true; }
    });
    if (changed) {
      const url = `${window.location.pathname}?${current.toString()}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', url);
    }
    // Preserve the landing test on ordinary links too, including full-page navigation.
    const carry = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(target instanceof HTMLAnchorElement) || target.hasAttribute('download')) return;
      if (!isInternalLink(target.href)) return;
      const variation = getAllParams()[LANDING_VARIATION_KEY];
      target.href = withLandingVariation(target.href, variation);
    };
    document.addEventListener('click', carry, true);
    document.addEventListener('auxclick', carry, true);
    return () => {
      document.removeEventListener('click', carry, true);
      document.removeEventListener('auxclick', carry, true);
    };
  }, [pathname, search]);
};

export const TrackingParams = () => { useTrackingParams(); return null; };
